import { useState, useEffect } from 'react';
import {
  Autocomplete,
  Button,
  Divider,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FilterableSelect } from '../common/FilterableSelect';
import CloseIcon from '@mui/icons-material/Close';
import { StatusSelect } from '../common/StatusSelect';
import { VocabAutocomplete } from '../common/VocabAutocomplete';
import { useQuery } from '@tanstack/react-query';
import { createApiClient } from '../../api/client';
import { useAuth } from '../../auth/useAuth';
import type { OutlineNodeDto } from '../../api/queries/outlines';

interface OutlineNodeDetailPanelProps {
  node: OutlineNodeDto | null;
  open: boolean;
  onClose: () => void;
  onSave: (
    updates: Partial<OutlineNodeDto> & { id: string; name: string },
  ) => void;
  onDelete: (id: string) => void;
}

const NODE_TYPES = ['part', 'chapter', 'scene', 'section', 'custom'] as const;
const SCENE_TYPES = [
  'action',
  'dialogue',
  'exposition',
  'montage',
  'flashback',
  'dream',
] as const;

const EMOTIONAL_BEAT_OPTIONS = [
  'tension',
  'relief',
  'joy',
  'sorrow',
  'fear',
  'anger',
  'surprise',
  'anticipation',
  'love',
  'betrayal',
  'triumph',
  'despair',
  'wonder',
  'disgust',
  'nostalgia',
  'resolve',
] as const;

interface EntityOption {
  id: string;
  name: string;
  entityType: string;
}

export function OutlineNodeDetailPanel({
  node,
  open,
  onClose,
  onSave,
  onDelete,
}: OutlineNodeDetailPanelProps) {
  const { getToken } = useAuth();
  const [form, setForm] = useState<Record<string, unknown>>({});

  // Entities for association
  const { data: entities = [] } = useQuery<EntityOption[]>({
    queryKey: ['entities-for-picker'],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const resp = await client
        .get('api/v2/entities')
        .json<{ data: EntityOption[] }>();
      return resp.data ?? [];
    },
  });

  useEffect(() => {
    if (node) {
      setForm({
        name: node.name,
        description: node.description ?? '',
        nodeType: node.nodeType ?? '',
        synopsis: node.synopsis ?? '',
        location: node.location ?? '',
        timePeriod: node.timePeriod ?? '',
        mood: node.mood ?? '',
        tone: node.tone ?? '',
        estimatedWordCount: node.estimatedWordCount ?? '',
        status: node.status ?? '',
        colorLabel: node.colorLabel ?? '',
        notes: node.notes ?? '',
        sceneType: node.sceneType ?? '',
        scenePurpose: node.scenePurpose ?? '',
        sceneStart: node.sceneStart ?? '',
        sceneEnd: node.sceneEnd ?? '',
        emotionalBeat: node.emotionalBeat ?? '',
        povCharacterId: node.povCharacterId ?? '',
      });
    }
  }, [node]);

  if (!node) return null;

  const handleSave = () => {
    onSave({
      id: node.id,
      name: (form.name as string) || node.name,
      description: (form.description as string) || null,
      nodeType: ((form.nodeType as string) ||
        null) as OutlineNodeDto['nodeType'],
      synopsis: (form.synopsis as string) || null,
      location: (form.location as string) || null,
      timePeriod: (form.timePeriod as string) || null,
      mood: (form.mood as string) || null,
      tone: (form.tone as string) || null,
      estimatedWordCount: form.estimatedWordCount
        ? Number(form.estimatedWordCount)
        : null,
      status: ((form.status as string) || null) as OutlineNodeDto['status'],
      colorLabel: (form.colorLabel as string) || null,
      notes: (form.notes as string) || null,
      sceneType: (form.sceneType as string) || null,
      scenePurpose: (form.scenePurpose as string) || null,
      sceneStart: (form.sceneStart as string) || null,
      sceneEnd: (form.sceneEnd as string) || null,
      emotionalBeat: (form.emotionalBeat as string) || null,
      povCharacterId: (form.povCharacterId as string) || null,
    });
  };

  const set = (key: string, value: unknown) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const characterEntities = entities.filter(
    (e) => e.entityType === 'Character',
  );
  const locationEntities = entities.filter((e) => e.entityType === 'Location');
  const selectedPov = characterEntities.find(
    (e) => e.id === (form.povCharacterId as string),
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 480, p: 2 } }}
    >
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ flex: 1 }}>
          Node Details
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>

      <Stack spacing={2} sx={{ overflow: 'auto', flex: 1, pb: 2 }}>
        <TextField
          label="Name"
          value={form.name ?? ''}
          onChange={(e) => set('name', e.target.value)}
          fullWidth
          required
        />

        <FilterableSelect
          options={NODE_TYPES.map((t) => ({
            value: t,
            label: t.charAt(0).toUpperCase() + t.slice(1),
          }))}
          value={(form.nodeType as string) ?? ''}
          onChange={(newValue) => set('nodeType', newValue)}
          label="Node Type"
          allowEmpty={true}
          emptyLabel="None"
        />

        <StatusSelect
          value={(form.status as string) || null}
          onChange={(v) => set('status', v ?? '')}
        />

        <TextField
          label="Synopsis"
          value={form.synopsis ?? ''}
          onChange={(e) => set('synopsis', e.target.value)}
          fullWidth
          multiline
          rows={3}
        />

        <TextField
          label="Description"
          value={form.description ?? ''}
          onChange={(e) => set('description', e.target.value)}
          fullWidth
          multiline
          rows={2}
        />

        <Divider />
        <Typography variant="subtitle2" color="text.secondary">
          Entities
        </Typography>

        <Autocomplete
          size="small"
          options={characterEntities}
          getOptionLabel={(o) => o.name}
          value={selectedPov ?? null}
          onChange={(_, v) => set('povCharacterId', v?.id ?? '')}
          renderInput={(params) => (
            <TextField {...params} label="POV Character" />
          )}
        />

        <Autocomplete
          size="small"
          options={locationEntities}
          getOptionLabel={(o) => (typeof o === 'string' ? o : o.name)}
          value={
            locationEntities.find(
              (e) => e.name === (form.location as string),
            ) ?? null
          }
          onChange={(_, v) =>
            set('location', typeof v === 'string' ? v : (v?.name ?? ''))
          }
          freeSolo
          renderInput={(params) => <TextField {...params} label="Location" />}
        />

        <Divider />
        <Typography variant="subtitle2" color="text.secondary">
          Setting
        </Typography>

        <TextField
          label="Time Period"
          value={form.timePeriod ?? ''}
          onChange={(e) => set('timePeriod', e.target.value)}
          fullWidth
        />

        <Stack direction="row" spacing={1}>
          <VocabAutocomplete
            kind="mood"
            value={(form.mood as string) || null}
            onChange={(v) => set('mood', v ?? '')}
            label="Mood"
          />
          <VocabAutocomplete
            kind="tone_modifiers"
            value={(form.tone as string) || null}
            onChange={(v) => set('tone', v ?? '')}
            label="Tone"
          />
        </Stack>

        <Divider />
        <Typography variant="subtitle2" color="text.secondary">
          Scene Details
        </Typography>

        <FilterableSelect
          options={SCENE_TYPES.map((t) => ({
            value: t,
            label: t.charAt(0).toUpperCase() + t.slice(1),
          }))}
          value={(form.sceneType as string) ?? ''}
          onChange={(newValue) => set('sceneType', newValue)}
          label="Scene Type"
          allowEmpty={true}
          emptyLabel="None"
        />

        <TextField
          label="Scene Purpose"
          value={form.scenePurpose ?? ''}
          onChange={(e) => set('scenePurpose', e.target.value)}
          fullWidth
        />

        <TextField
          label="Scene Start"
          value={form.sceneStart ?? ''}
          onChange={(e) => set('sceneStart', e.target.value)}
          fullWidth
          multiline
          rows={2}
          placeholder="What's happening when the scene opens..."
        />

        <TextField
          label="Scene End"
          value={form.sceneEnd ?? ''}
          onChange={(e) => set('sceneEnd', e.target.value)}
          fullWidth
          multiline
          rows={2}
          placeholder="How the scene concludes..."
        />

        <Autocomplete
          freeSolo
          size="small"
          options={[...EMOTIONAL_BEAT_OPTIONS]}
          value={(form.emotionalBeat as string) || ''}
          onChange={(_, v) => set('emotionalBeat', v ?? '')}
          onInputChange={(_, v, reason) => {
            if (reason === 'input') set('emotionalBeat', v ?? '');
          }}
          renderInput={(params) => (
            <TextField {...params} label="Emotional Beat" />
          )}
        />

        <Divider />

        <TextField
          label="Estimated Word Count"
          type="number"
          value={form.estimatedWordCount ?? ''}
          onChange={(e) => set('estimatedWordCount', e.target.value)}
          fullWidth
        />

        <TextField
          label="Notes"
          value={form.notes ?? ''}
          onChange={(e) => set('notes', e.target.value)}
          fullWidth
          multiline
          rows={3}
          placeholder="Private notes..."
        />
      </Stack>

      <Divider sx={{ my: 1 }} />
      <Stack direction="row" spacing={1} justifyContent="space-between">
        <Button color="error" onClick={() => onDelete(node.id)}>
          Delete Node
        </Button>
        <Button variant="contained" onClick={handleSave}>
          Save
        </Button>
      </Stack>
    </Drawer>
  );
}
